import { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Spinner,
  Alert,
  Badge,
  Form,
  Modal,
  Row,
  Col,
} from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { listUsers, createUser, updateUser } from '../services/api';
import { BusyButton } from '../components/BusyButton';
import { useConfirm } from '../components/ConfirmProvider';
import { useToast } from '../components/ToastProvider';
import { errMessage } from '../errors';

type PlatformUser = components['schemas']['PlatformUser'];
type Role = components['schemas']['Role'];

const ROLES: Role[] = ['LOCAL_DOCTOR', 'REVIEWER', 'ADMIN', 'GLOBAL_ADMIN'];

const roleVariant: Record<Role, string> = {
  LOCAL_DOCTOR: 'info',
  REVIEWER: 'primary',
  ADMIN: 'warning',
  GLOBAL_ADMIN: 'danger',
};

function fullName(u: PlatformUser): string {
  const name = [u.givenName, u.familyName].filter(Boolean).join(' ').trim();
  return name || '—';
}

export function AdminUsers() {
  const [items, setItems] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<PlatformUser | null>(null);

  const load = () => {
    setLoading(true);
    listUsers()
      .then((res) => setItems(res.items))
      .catch((e) => setError(errMessage(e, 'Failed to load users')))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <Card.Title as="h2" className="h4 mb-0">Users</Card.Title>
          <div className="flex-grow-1" />
          <Button size="sm" onClick={() => setShowCreate(true)}>
            Add user
          </Button>
        </div>

        {loading && (
          <div className="text-muted d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading users…
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && (
          <Table hover responsive className="align-middle mb-0">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Enabled</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.username}>
                  <td>{u.email}</td>
                  <td>{fullName(u)}</td>
                  <td>
                    <Badge bg={roleVariant[u.role] ?? 'secondary'}>{u.role}</Badge>
                  </td>
                  <td className="text-muted small">{u.status}</td>
                  <td>
                    {u.enabled ? (
                      <Badge bg="success">Enabled</Badge>
                    ) : (
                      <Badge bg="secondary">Disabled</Badge>
                    )}
                  </td>
                  <td className="text-end">
                    <Button variant="outline-secondary" size="sm" onClick={() => setEditing(u)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </Card>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [role, setRole] = useState<Role>('LOCAL_DOCTOR');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    createUser({
      email,
      givenName,
      familyName,
      role,
      temporaryPassword: temporaryPassword || undefined,
    })
      .then(() => {
        toast.success(`User ${email} created.`);
        onCreated();
      })
      .catch((err) => setError(errMessage(err, 'Failed to create user')))
      .finally(() => setSaving(false));
  };

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Add user</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Form.Group>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>First name</Form.Label>
                <Form.Control
                  value={givenName}
                  onChange={(e) => setGivenName(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Last name</Form.Label>
                <Form.Control
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-1">
            <Form.Label>Temporary password (optional)</Form.Label>
            <Form.Control
              type="text"
              value={temporaryPassword}
              onChange={(e) => setTemporaryPassword(e.target.value)}
              placeholder="Leave blank to require reset invite"
            />
            <Form.Text className="text-muted">
              If set, the user must change it on first sign-in.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <BusyButton type="submit" loading={saving} busyLabel="Creating…">
            Create user
          </BusyButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: PlatformUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [givenName, setGivenName] = useState(user.givenName ?? '');
  const [familyName, setFamilyName] = useState(user.familyName ?? '');
  const [role, setRole] = useState<Role>(user.role);
  const [enabled, setEnabled] = useState(user.enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  const disabling = user.enabled && !enabled;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Disabling a user blocks their sign-in; confirm so it isn't accidental.
    if (
      disabling &&
      !(await confirm({
        title: 'Disable this user?',
        body: `${user.email} will no longer be able to sign in. Their records and history are kept. You can re-enable them later.`,
        confirmLabel: 'Disable user',
        variant: 'danger',
      }))
    ) {
      return;
    }

    setSaving(true);
    setError(null);
    updateUser(user.username, {
      givenName: givenName || undefined,
      familyName: familyName || undefined,
      role,
      enabled,
    })
      .then(() => {
        toast.success(`User ${user.email} updated.`);
        onSaved();
      })
      .catch((err) => setError(errMessage(err, 'Failed to update user')))
      .finally(() => setSaving(false));
  };

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Edit {user.email}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>First name</Form.Label>
                <Form.Control value={givenName} onChange={(e) => setGivenName(e.target.value)} />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Last name</Form.Label>
                <Form.Control value={familyName} onChange={(e) => setFamilyName(e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Check
            type="switch"
            id="user-enabled"
            label="Account enabled"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <BusyButton type="submit" loading={saving} busyLabel="Saving…">
            Save changes
          </BusyButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
